/**
 * Availability computation utilities
 */

import type { EmployeeSummary } from "@/hooks/useMakersBoardData";
import { assignmentCountsForMode, getHoursForWeek, type AvailabilityMode } from "./makersUtils";
import type { WeekColumn } from "./weekUtils";
import { getEmployeeDisplayName } from "./nameUtils";

export interface AvailableEmployee {
  id: string;
  name: string;
  availableHours: number;
  capacity: number;
  roles: string[];
}

export interface WeekAvailability {
  week: WeekColumn;
  employees: AvailableEmployee[];
}

export type MinimumAvailableHours =
  | number
  | ((week: WeekColumn, weekIndex: number) => number);

/**
 * For each week, find employees with at least `minAvailableHours` of unassigned capacity.
 * Available hours = weekly_capacity_hours - total assigned hours for that week.
 * Default threshold = 40 means only show people with >= 40 hours free.
 * A threshold of 0 means every employee shows up.
 */
export function computeWeeklyAvailability(
  employees: EmployeeSummary[],
  weeks: WeekColumn[],
  minAvailableHours: MinimumAvailableHours = 40,
  mode: AvailabilityMode = "all",
): WeekAvailability[] {
  const resolveMinAvailableHours =
    typeof minAvailableHours === "function"
      ? minAvailableHours
      : () => minAvailableHours;

  return weeks.map((week, weekIndex) => {
    const available: AvailableEmployee[] = [];
    const weekMinAvailableHours = resolveMinAvailableHours(week, weekIndex);

    for (const employee of employees) {
      const capacity = employee.weeklyCapacityHours;
      let totalAllocated = 0;

      for (const assignment of employee.assignments) {
        const projectStart = (assignment.project as any)?.start_date;
        const projectNumWeeks = (assignment.project as any)?.num_weeks;
        if (!projectStart || !projectNumWeeks) continue;

        // Skip assignments that don't count under the current availability mode
        // (tentative work, or non-sold work when showing sold availability).
        if (!assignmentCountsForMode(assignment, mode)) {
          continue;
        }

        const hours = getHoursForWeek(assignment, projectStart, projectNumWeeks, week);
        totalAllocated += hours;
      }

      const availableHours = capacity - totalAllocated;
      if (availableHours >= weekMinAvailableHours) {
        available.push({
          id: employee.id,
          name: getEmployeeDisplayName(employee) || "Unknown",
          availableHours,
          capacity,
          roles: (employee.role ?? []) as string[],
        });
      }
    }

    return { week, employees: available };
  });
}
